/**
 * Masked error log client.
 *
 * Fire-and-forget. Mirrors `telemetry.ts` in shape: never throws upward,
 * silently swallows network failures, enriches with the same shared
 * client-context fields. Server-side Pydantic model enforces strict
 * allowlist; this module masks workbook-derived strings before send.
 *
 * Wired in three places:
 *   1. Global window.onerror / unhandledrejection (auto-captures uncaught
 *      failures anywhere in the bundle).
 *   2. taskpane.ts runAction catch (so every action button click that
 *      throws gets a logged stack).
 *   3. importPdf catch (PDF-specific error path).
 *
 * Masking rules (brief §15):
 *   - Cell refs like `Sheet1!A1` or `$A$1` → `<CELL>`
 *   - Double-quoted string literals → `"<STR>"`
 */
import { postJson } from "./outbound";
import {
  APP_VERSION,
  getExcelPlatform,
  getOfficeVersionBucket,
} from "./clientContext";
import { pushError } from "./diagnosticsBuffer";

const SERVER_URL = process.env.EXTRACT_SERVER_URL.replace(/\/+$/, "");

interface ErrorLogPayload {
  error_code: string;
  error_message_masked: string;
  stack_summary?: string;
  app_version: string;
  excel_platform: string;
  office_version_bucket: string;
}

/**
 * Apply the brief's two masking rules to an error message.
 *
 * Exported for unit-testing; production callers should use `reportError`.
 */
export function maskMessage(raw: string): string {
  let s = raw;
  // Cell references with optional sheet prefix:
  //   "Sheet1!A1", "Revenue!$D$20", "A1", "$AB$100"
  s = s.replace(/(?:[A-Za-z0-9_]+!)?\$?[A-Z]{1,3}\$?\d+/g, "<CELL>");
  // Double-quoted string literals (handles escaped quotes inside).
  s = s.replace(/"[^"\\]*(?:\\.[^"\\]*)*"/g, '"<STR>"');
  return s;
}

/**
 * Reduce a JS stack trace to its top 5 frames.
 *
 * Stack trace contents are code paths (filename:line:col) and function
 * names — no user values — so we keep them verbatim and trust the
 * source-side mask not to leak.
 */
export function summarizeStack(stack: string | undefined): string | undefined {
  if (!stack) return undefined;
  // First line is usually "ErrorName: message" — keep it (already masked
  // separately) plus the top 5 stack frames.
  const lines = stack.split("\n").slice(0, 6);
  return lines.join("\n");
}

export interface ReportErrorOptions {
  /** Structured error code from §14 (e.g. ACTION_THREW, UNCAUGHT_ERROR). */
  error_code: string;
  /** The thrown value. Pulls .message + .stack if it's an Error. */
  error: unknown;
}

export function reportError(opts: ReportErrorOptions): void {
  const rawMessage =
    opts.error instanceof Error ? opts.error.message : String(opts.error);
  const rawStack =
    opts.error instanceof Error ? opts.error.stack : undefined;

  const payload: ErrorLogPayload = {
    error_code: opts.error_code,
    error_message_masked: maskMessage(rawMessage),
    stack_summary: summarizeStack(rawStack),
    app_version: APP_VERSION,
    excel_platform: getExcelPlatform(),
    office_version_bucket: getOfficeVersionBucket(),
  };

  // Local ring buffer for the diagnostics packet — keep even when the
  // network post below fails.
  pushError({
    error_code: payload.error_code,
    error_message_masked: payload.error_message_masked,
    stack_summary: payload.stack_summary,
  });

  void postJson(`${SERVER_URL}/api/error/log`, payload).catch((err) => {
    console.warn("errorLog: report failed", opts.error_code, err);
  });
}

/**
 * Wire `window.onerror` and `window.onunhandledrejection` so any
 * uncaught error anywhere in the bundle becomes an ERROR_LOGGED row.
 * Call once at startup.
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener("error", (e: ErrorEvent) => {
    reportError({
      error_code: "UNCAUGHT_ERROR",
      error: e.error ?? new Error(e.message),
    });
  });
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    reportError({
      error_code: "UNHANDLED_REJECTION",
      error: e.reason,
    });
  });
}
